import { useEffect } from 'react';

import cn from 'classnames';
import { countries } from 'constants/countries';
import { US_STATES } from 'constants/country-admin-divisions';
import patternValidators from 'helpers/validators/pattern-validators';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { Button, Input, InputGroup, Select } from 'components/FormComponents';

import { updateCompany } from 'api/company-slice';

import styles from './Settings.module.scss';

export const Settings = ({ company, setLoading = () => {} }) => {
  const dispatch = useDispatch();

  const {
    register,
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();
  const countryValue = useWatch({ control, name: 'country' });
  const localeValue = useWatch({ control, name: 'locale' });
  const stateValue = useWatch({ control, name: 'state' });

  const isUSCompany = countryValue === 'US';

  const onCompanyUpdate = params => dispatch(updateCompany({ id: company.id, params }));

  useEffect(() => {
    reset({
      name: company.name,
      country: company.country,
      state: company.state,
      locale: company.locale,
      contact_email: company.contact_email,
    });
  }, [company]);

  // Clear state when country changes away from US
  useEffect(() => {
    if (!isUSCompany && stateValue) {
      setValue('state', null);
    }
  }, [isUSCompany, stateValue, setValue]);

  const onSubmit = async data => {
    setLoading(true);
    await onCompanyUpdate(data);
    setLoading(false);
  };

  return (
    <form className={cn(styles.root)} onSubmit={handleSubmit(onSubmit)}>
      <div className={cn('card--small', 'card', 'card-with-border')}>
        <div className="u-margin--large">
          <div className={cn('u-flex-box', 'u-flex-justify-between', 'u-flex-align-center')}>
            <h1 className="title-2">Company Settings</h1>
          </div>
          <p className="t-small">Edit the details of the company settings below.</p>
        </div>
        <div className={cn(styles.content, 'background-secondary', 'u-padding')}>
          <div className={cn('card', 'card-with-border', 'u-padding')}>
            <InputGroup title="Name">
              <Input
                name="name"
                placeholder="e.g. Checkmate"
                register={register}
                validators={{ required: true }}
                error={errors?.name?.type}
                inputProps={{ tabIndex: 1 }}
              />
            </InputGroup>
            <InputGroup title="Country">
              <Select
                name="country"
                placeholder="Country"
                register={register}
                validators={{ required: true }}
                value={countryValue}>
                {countries.map(country => (
                  <Select.Item key={country.alpha2} value={country.alpha2}>
                    {country.name}
                  </Select.Item>
                ))}
              </Select>
            </InputGroup>
            {isUSCompany && (
              <InputGroup title="State">
                <Select
                  name="state"
                  placeholder="Select State"
                  register={register}
                  value={stateValue}>
                  <Select.Item value="">Select State</Select.Item>
                  {US_STATES.map(state => (
                    <Select.Item key={state.value} value={state.value}>
                      {state.name}
                    </Select.Item>
                  ))}
                </Select>
              </InputGroup>
            )}
            <InputGroup title="Locale">
              <Select
                name="locale"
                register={register}
                validators={{ required: true }}
                value={localeValue}>
                <Select.Item value="en_GB">en_GB</Select.Item>
                <Select.Item value="en_US">en_US</Select.Item>
              </Select>
            </InputGroup>
            <InputGroup title="Support contact email">
              <Input
                name="contact_email"
                placeholder="support@checkmate.tech"
                register={register}
                validators={patternValidators.email}
                error={errors?.contact_email?.message}
                inputProps={{ tabIndex: 4 }}
              />
            </InputGroup>
          </div>
        </div>
        <div className={cn('card_footer', 'u-flex-box', 'u-flex-justify-end')}>
          <Button submit>Save</Button>
        </div>
      </div>
    </form>
  );
};
